import api from './api';

/**
 * Payment service for handling all payment-related API calls
 */
const paymentService = {
  /**
   * Get a list of payments with optional filtering, sorting, and pagination
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (starts at 1)
   * @param {number} options.limit - Number of items per page
   * @param {string} options.sort - Sort field and direction (e.g., '-createdAt' for descending by createdAt)
   * @param {Object} options.filter - Filter criteria
   * @returns {Promise<Object>} Paginated result with items and metadata
   */
  getPayments: async (options = {}) => {
    try {
      const { page = 1, limit = 10, sort, filter = {} } = options;
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      
      if (sort) {
        params.append('sort', sort);
      }
      
      // Add any filter criteria
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
      
      const response = await api.get(`/payments?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch payments');
    }
  },
  
  /**
   * Get a payment by ID
   * @param {string} id - The payment ID
   * @returns {Promise<Object>} Payment data
   */
  getPaymentById: async (id) => {
    try {
      const response = await api.get(`/payments/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch payment');
    }
  },
  
  /**
   * Create a new payment
   * @param {Object} paymentData - The payment data
   * @returns {Promise<Object>} Created payment data
   */
  createPayment: async (paymentData) => {
    try {
      const response = await api.post('/payments', paymentData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to create payment');
    }
  },
  
  /**
   * Update an existing payment
   * @param {string} id - The payment ID
   * @param {Object} paymentData - The updated payment data
   * @returns {Promise<Object>} Updated payment data
   */
  updatePayment: async (id, paymentData) => {
    try {
      const response = await api.put(`/payments/${id}`, paymentData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update payment');
    }
  },
  
  /**
   * Update payment status
   * @param {string} id - The payment ID
   * @param {string} status - The new payment status
   * @returns {Promise<Object>} Updated payment data
   */
  updatePaymentStatus: async (id, status) => {
    try {
      const response = await api.patch(`/payments/${id}/status`, { status });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update payment status');
    }
  },
  
  /**
   * Delete a payment
   * @param {string} id - The payment ID
   * @returns {Promise<boolean>} True if deletion was successful
   */
  deletePayment: async (id) => {
    try {
      await api.delete(`/payments/${id}`);
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to delete payment');
    }
  },
  
  /**
   * Get payment statistics (for dashboard)
   * @returns {Promise<Object>} Payment statistics
   */
  getPaymentStats: async () => {
    try {
      const response = await api.get('/payments/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch payment statistics');
    }
  },
  
  /**
   * Export payments data (CSV/Excel)
   * @param {Object} options - Export options
   * @param {string} options.format - Export format (csv, excel)
   * @param {Object} options.filter - Filter criteria
   * @returns {Promise<Blob>} Exported file as Blob
   */
  exportPayments: async (options = { format: 'csv' }) => {
    try {
      const { format, filter = {} } = options;
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('format', format);
      
      // Add any filter criteria
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(`filter[${key}]`, value);
        }
      });
      
      const response = await api.get(`/payments/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to export payments');
    }
  }
};

export default paymentService;